import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const navLinks = [
  { name: 'Home', to: '/' },
  { name: 'Collections', to: '/collection' },
  { name: 'Wholesale', to: '/wholesale' },
  { name: 'About', to: '/about' },
  { name: 'Sustainability', to: '/sustainability' },
  { name: 'Contact', to: '/contact' },
];

export const Navbar = ({ onRequestQuote }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`relative w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#F5F2ED] shadow-sm'
          : 'bg-[#F5F2ED]/90 backdrop-blur-sm'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <img
              src={`${process.env.PUBLIC_URL || ''}/bagsforever.png`}
              alt="Bags4Ever - Sustainable by Nature"
              className="h-16 sm:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.to}
                className="text-sm text-[#2B2B2B] hover:text-[#2F4F3E] transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <Button
              onClick={onRequestQuote}
              className="bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md px-5 py-2 text-sm font-medium"
              data-testid="request-quote-btn"
            >
              Request Quote
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-[#2B2B2B]"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[280px] bg-[#F5F2ED] border-l border-[#E3DED8]"
              >
                <div className="flex flex-col h-full pt-8">
                  <div className="flex items-center gap-2 mb-8">
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/bagsforever.png`}
                      alt="Bags4Ever - Sustainable by Nature"
                      className="h-9 w-auto"
                    />
                  </div>
                  <nav className="flex flex-col gap-1">
                    {navLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.to}
                        onClick={handleNavClick}
                        className="text-base text-[#2B2B2B] hover:text-[#2F4F3E] transition-colors py-3 border-b border-[#E3DED8]"
                        data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-8">
                    <Button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        onRequestQuote();
                      }}
                      className="w-full bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md py-5 font-medium"
                      data-testid="mobile-request-quote-btn"
                    >
                      Request Quote
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};
